import type { ComputedRef } from 'vue'

type StartChat = (options: {
  chatId: string | null
  canResumeLatest?: boolean
  withPageContext?: boolean
}) => Promise<void>

/**
 * Orchestrates a "seeded launch": opening the assistant into a brand-new chat
 * pre-supplied with page context and/or an initial message, typically from an
 * external entry point (push notification, email, in-page CTA).
 *
 * This composable owns the sequence and the decisions; the side-effect
 * primitives (`startChat`, `sendSeededMessage`) are injected and owned by the
 * page, so the composable stays unit-testable in isolation.
 *
 * When `canResumeLatest` is true, the latest chat is resumed instead of
 * creating a new one, and the seeded message is appended without clearing the
 * loaded conversation history. When false (default), a fresh chat is created
 * with page context attached.
 */
export function useSeededLaunch({
  isSeededLaunch,
  canResumeLatest = false,
  seededMessage,
  startChat,
  sendSeededMessage,
}: {
  isSeededLaunch: ComputedRef<boolean>
  canResumeLatest?: boolean
  seededMessage: ComputedRef<string>
  startChat: StartChat
  sendSeededMessage: (
    content: string,
    options?: { clear?: boolean },
  ) => Promise<void>
}) {
  /**
   * Runs the seeded launch when the route carries seeded params. Returns `true`
   * when it handled the launch (so the caller skips the normal resume path),
   * `false` otherwise.
   */
  async function maybeLaunch(): Promise<boolean> {
    if (!isSeededLaunch.value) {
      return false
    }

    await startChat({
      chatId: null,
      canResumeLatest,
      withPageContext: true,
    })

    const message = seededMessage.value
    if (message) {
      await sendSeededMessage(message, { clear: !canResumeLatest })
    }

    return true
  }

  return {
    maybeLaunch,
  }
}
